import type { Database } from 'sql.js';

export type SqlJsBind = string | number | bigint | Uint8Array | null | undefined;

/** Executes an insert statement and returns the last inserted row ID. */
export function insertRowId(db: Database, sql: string, params: SqlJsBind[]): number {
  const normalized = params.map((p) => {
    if (typeof p === 'bigint') return Number(p);
    if (p === undefined || (typeof p === 'number' && !Number.isFinite(p))) return null;
    return p;
  });
  db.run(sql, normalized);
  // sql.js 1.x: `db.run` may not expose `lastInsertRowid` on its return value; use SQLite's function.
  const row = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
  const v = row?.k ?? row?.K;
  if (v == null) return 0;
  if (typeof v === 'bigint') return Number(v);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Executes a query and returns the first row as an object, or null if empty. */
export function queryOne(db: Database, sql: string, params: SqlJsBind[]): Record<string, SqlJsBind> | null {
  const st = db.prepare(sql);
  try {
    st.bind(params);
    if (!st.step()) return null;
    return st.getAsObject() as Record<string, SqlJsBind>;
  } finally {
    st.free();
  }
}

/** Executes a query and returns all rows as an array of objects. */
export function queryAll(db: Database, sql: string, params: SqlJsBind[]): Record<string, SqlJsBind>[] {
  const st = db.prepare(sql);
  try {
    st.bind(params);
    const rows: Record<string, SqlJsBind>[] = [];
    while (st.step()) {
      rows.push(st.getAsObject() as Record<string, SqlJsBind>);
    }
    return rows;
  } finally {
    st.free();
  }
}

/** Normalizes an SQLite INTEGER value to a positive JavaScript number, or null if invalid. */
export function sqlPositiveIntId(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'bigint') {
    const n = Number(v);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Normalizes an SQLite INTEGER value to a JavaScript number, defaulting to zero. */
export function sqlIntOrZero(v: unknown): number {
  if (v == null || v === '') return 0;
  if (typeof v === 'bigint') return Number(v);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Returns the first defined value from a row object matching any of the provided keys. */
export function rowFirstKeyColumn(row: Record<string, SqlJsBind>, ...keys: string[]): SqlJsBind | undefined {
  for (const k of keys) {
    if (Object.hasOwn(row, k) && row[k] !== undefined) return row[k];
  }
  return undefined;
}

/** Ensures a value is stored as a string, preserving data URLs for browser-based SQLite. */
export function persistBrowserDataUrl(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  return '';
}

/** Resolves a stored file path or URL to a public URL using the provided prefix. */
export function publicFileUrl(stored: unknown, filesPublicPrefix: string): string {
  if (stored == null || stored === '') return '';
  if (typeof stored !== 'string') return '';
  const s = stored;
  if (s.startsWith('data:') || /^https?:\/\//i.test(s) || s.startsWith('blob:')) return s;
  if (s.startsWith('participantes/')) return `${filesPublicPrefix}/${s}`;
  return s;
}
